import { catalog } from "../i18n.js";

/** The roster sidebar (components/Sidebar.tsx): its rows, sections, menus and foot. */
export const SIDEBAR = catalog(
  {
    bots: "Bots",
    newLabel: "New",
    newTitle: "New bot or group",
    search: "Search",
    searchBots: "Search bots",
    unreachableTitle: "Can’t reach your computer",
    unreachableAbout: "Your bots are safe; they just can’t be loaded right now.",
    checkHost: "Check the host address",
    nothingMatches: (query: string) => `Nothing matches “${query}”.`,
    pinnedBots: "Pinned bots",
    dropToPin: "Drop here to pin",
    unassigned: "Unassigned",
    hidden: "Hidden",
    connecting: "Connecting to your computer…",
    noBots: "No bots yet. Press + to make one.",
    plugins: "Plugins",
    pluginsTitle: "Connect your accounts, like Gmail or Slack, so your bots can work in them",
    hiddenBots: "Hidden bots",
    host: "Host",
    hostTitle: "Which computer this app talks to",

    backToMenu: "Back to the menu",
    back: "Back",

    working: "Working…",
    nobodySpoken: "Nobody has spoken yet.",
    noMessages: "No messages yet.",
    workingDot: "working",
    needsYouDot: "needs you",
    unreadDot: "unread",
    optionsFor: (name: string) => `Options for ${name}`,
    options: "Options",
    optionsForSection: (name: string) => `Options for section ${name}`,
    dropHere: "Drop here",
    nothingHere: "Nothing here yet",
    emptyGroup: "empty",

    pin: "Pin",
    unpin: "Unpin",
    moveTo: "Move to",
    newSection: "New section",
    newSectionNote: (name: string) => `${name} goes in it.`,
    sectionExamples: "Work, Shop, Family…",
    create: "Create",
    markUnread: "Mark as unread",
    editGroup: "Edit group",
    editProfile: "Edit profile",
    duplicate: "Duplicate",
    copyId: "Copy conversation ID",
    showInSidebar: "Show in sidebar",
    hideFromSidebar: "Hide from sidebar",
    delete: "Delete",
    renameSection: "Rename section",
    rename: "Rename",
    deleteSection: "Delete section",
    moveSectionUp: "Move up",
    moveSectionDown: "Move down",
    dragSection: (name: string) => `${name}: drag to rearrange, or Alt+Up and Alt+Down`,
    expandSection: (name: string) => `Show ${name}`,
    collapseSection: (name: string) => `Fold ${name} away`,
    activity: "Activity",
    activityLabel: "Activity: only the bots with something going on",
    activityTitle: "Activity: bots waiting on you, at work, or with something unread",
    nothingGoingOn: "Nothing going on right now.",
    hideSidebar: "Hide the sidebar",
    hideSidebarTitle: (keys: string) => `Hide the sidebar (${keys})`,
  },
  {
    bots: "Bot",
    newLabel: "Baru",
    newTitle: "Bot atau grup baru",
    search: "Cari",
    searchBots: "Cari bot",
    unreachableTitle: "Tidak bisa terhubung ke komputer kamu",
    unreachableAbout: "Bot kamu aman; hanya belum bisa dimuat sekarang.",
    checkHost: "Periksa alamat host",
    nothingMatches: (query: string) => `Tidak ada yang cocok dengan “${query}”.`,
    pinnedBots: "Bot yang disematkan",
    dropToPin: "Lepas di sini untuk menyematkan",
    unassigned: "Tanpa bagian",
    hidden: "Tersembunyi",
    connecting: "Menghubungkan ke komputer kamu…",
    noBots: "Belum ada bot. Tekan + untuk membuatnya.",
    plugins: "Plugin",
    pluginsTitle: "Hubungkan akun kamu, seperti Gmail atau Slack, supaya bot kamu bisa bekerja di sana",
    hiddenBots: "Bot tersembunyi",
    host: "Host",
    hostTitle: "Komputer yang dihubungi aplikasi ini",

    backToMenu: "Kembali ke menu",
    back: "Kembali",

    working: "Sedang bekerja…",
    nobodySpoken: "Belum ada yang bicara.",
    noMessages: "Belum ada pesan.",
    workingDot: "sedang bekerja",
    needsYouDot: "butuh kamu",
    unreadDot: "belum dibaca",
    optionsFor: (name: string) => `Opsi untuk ${name}`,
    options: "Opsi",
    optionsForSection: (name: string) => `Opsi untuk bagian ${name}`,
    dropHere: "Lepas di sini",
    nothingHere: "Belum ada apa-apa di sini",
    emptyGroup: "kosong",

    pin: "Sematkan",
    unpin: "Lepas sematan",
    moveTo: "Pindahkan ke",
    newSection: "Bagian baru",
    newSectionNote: (name: string) => `${name} akan masuk ke bagian ini.`,
    sectionExamples: "Kerja, Toko, Keluarga…",
    create: "Buat",
    markUnread: "Tandai belum dibaca",
    editGroup: "Ubah grup",
    editProfile: "Ubah profil",
    duplicate: "Duplikat",
    copyId: "Salin ID percakapan",
    showInSidebar: "Tampilkan di daftar",
    hideFromSidebar: "Sembunyikan dari daftar",
    delete: "Hapus",
    renameSection: "Ganti nama bagian",
    rename: "Ganti nama",
    deleteSection: "Hapus bagian",
    moveSectionUp: "Pindah ke atas",
    moveSectionDown: "Pindah ke bawah",
    dragSection: (name: string) => `${name}: seret untuk menata ulang, atau Alt+Atas dan Alt+Bawah`,
    expandSection: (name: string) => `Tampilkan ${name}`,
    collapseSection: (name: string) => `Lipat ${name}`,
    activity: "Aktivitas",
    activityLabel: "Aktivitas: hanya bot yang sedang ada kegiatan",
    activityTitle: "Aktivitas: bot yang menunggu kamu, sedang bekerja, atau punya pesan belum dibaca",
    nothingGoingOn: "Belum ada kegiatan saat ini.",
    hideSidebar: "Sembunyikan sidebar",
    hideSidebarTitle: (keys: string) => `Sembunyikan sidebar (${keys})`,
  },
);

/** Starting a chat: a new bot or a new group (components/NewChat.tsx). */
export const NEW_CHAT = catalog(
  {
    titleMenu: "New chat",
    titleAgent: "New bot",
    titleMembers: "Add group members",
    titleGroup: "New group",
    newBot: "New bot",
    newGroup: "New group",
    nothingMatches: (query: string) => `Nothing matches “${query}”.`,
    noBots: "No bots yet.",
    groupName: "Group name",
    name: "Name",
    groupExample: "Launch",
    title: "Title",
    optional: "(Optional)",
    titleExample: "Personal Assistant",
    description: "Description",
    groupDescriptionExample: "What this group is for; every member reads it.",
    botDescriptionExample: "Who this bot is and what it does; this is what the model sees.",
    members: "Members",
    edit: "Edit",
    creating: "Creating…",
    create: "Create",
    close: "Close",
    back: "Back",
    searchBots: "Search bots",
    groupNeedsBots: "A group needs bots to put in it",
    groupAbout: "A chat several bots talk in",
    bots: "Bots",
    remove: (name: string) => `Remove ${name}`,
    next: "Next",
    pickMember: "Pick at least one member",
    nameGroup: "Name the group",
  },
  {
    titleMenu: "Chat baru",
    titleAgent: "Bot baru",
    titleMembers: "Tambah anggota grup",
    titleGroup: "Grup baru",
    newBot: "Bot baru",
    newGroup: "Grup baru",
    nothingMatches: (query: string) => `Tidak ada yang cocok dengan “${query}”.`,
    noBots: "Belum ada bot.",
    groupName: "Nama grup",
    name: "Nama",
    groupExample: "Peluncuran",
    title: "Jabatan",
    optional: "(Opsional)",
    titleExample: "Asisten Pribadi",
    description: "Deskripsi",
    groupDescriptionExample: "Untuk apa grup ini; setiap anggota membacanya.",
    botDescriptionExample: "Siapa bot ini dan apa tugasnya; inilah yang dilihat model.",
    members: "Anggota",
    edit: "Ubah",
    creating: "Membuat…",
    create: "Buat",
    close: "Tutup",
    back: "Kembali",
    searchBots: "Cari bot",
    groupNeedsBots: "Grup butuh bot sebagai anggotanya",
    groupAbout: "Chat tempat beberapa bot mengobrol",
    bots: "Bot",
    remove: (name: string) => `Hapus ${name}`,
    next: "Lanjut",
    pickMember: "Pilih minimal satu anggota",
    nameGroup: "Beri nama grup",
  },
);
